import { useParams } from 'react-router-dom'

import { AvatarStack, Box, Text, useBreakpoint } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  useProcessTargetForPeerAssignmentDataTable,
} from 'generated/resources/datatable.graphql'
import { compact } from 'lodash'

import DataTable from 'modules/core/components/DataTable'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { TargetAvatar } from 'modules/performance_process/components/common/TargetAvatar'
import { getMemberAvatarsFragment } from 'modules/performance_process/components/review/common/getMemberAvatars'
import { MAX_PEERS_TO_SHOW } from 'modules/performance_process/components/review/constants'
import { PerformanceReviewProcessTargetPeerAssignmentStatusEnum } from 'modules/performance_process/lib/peerAssignmentTag'

import { Actions } from './helpers/Actions'
import { StatusTag } from './helpers/StatusTag'

type Props = {
  canAddPeers: boolean
}

const ProcessTargetForPeerAssignmentDataTable = ({ canAddPeers }: Props) => {
  const { reviewId } = useParams()
  const { isMobile } = useBreakpoint()
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const source = useProcessTargetForPeerAssignmentDataTable(
    compact([
      {
        identifier: 'target',
        title: i18n.t('performance_process.reviews.peer_management.peers_datatable.employee'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ access }}',
        colSpan: 2,
        render: ({ access }) => {
          return access && <TargetAvatar access={access} />
        },
      },
      !isMobile && {
        identifier: 'manager',
        title: i18n.t('performance_process.reviews.peer_management.peers_datatable.manager'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ access_employee_manager_access_fullName }}',
        colSpan: 2,
        render: ({ access_employeeName_managerName_fullName }) => {
          return access_employeeName_managerName_fullName ? (
            <Box>
              <Text.Normal ellipsis>{access_employeeName_managerName_fullName}</Text.Normal>
            </Box>
          ) : (
            <Text.Normal color="grey600">
              {i18n.t('performance_process.reviews.peer_management.no_data')}
            </Text.Normal>
          )
        },
      },
      {
        identifier: 'status',
        title: i18n.t('performance_process.reviews.peer_management.peers_datatable.status'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ Status }}',
        colSpan: 1,
        render: ({ evaluations }) => {
          return (
            <StatusTag
              status={
                evaluations && evaluations.length != 0
                  ? PerformanceReviewProcessTargetPeerAssignmentStatusEnum.Assigned
                  : PerformanceReviewProcessTargetPeerAssignmentStatusEnum.Pending
              }
            />
          )
        },
      },
      !isMobile && {
        identifier: 'peersAssigned',
        title: i18n.t('performance_process.reviews.peer_management.peers_datatable.peers'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ Peers }}',
        colSpan: 2,
        render: ({ evaluations }) => {
          const reviewerEmployeeNames = evaluations.map(
            (evaluation) => evaluation.reviewerEmployeeName
          )

          const avatarsToShow = getMemberAvatarsFragment(
            reviewerEmployeeNames.slice(0, MAX_PEERS_TO_SHOW)
          )
          const membersLeftover = reviewerEmployeeNames.length - MAX_PEERS_TO_SHOW

          return (
            <Box flexDirection="row" alignItems="center" gap="s16">
              {!reviewerEmployeeNames.length ? (
                <Text.Normal color="grey600">
                  {i18n.t('performance_process.reviews.peer_management.no_data')}
                </Text.Normal>
              ) : (
                <AvatarStack
                  size="s32"
                  overlap="s8"
                  textColor="viridian1000"
                  textSize="100"
                  backgroundColor="viridian200"
                  avatars={avatarsToShow}
                  leftover={membersLeftover}
                  gap="s8"
                />
              )}
            </Box>
          )
        },
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 100,
        fixed: true,
        render: ({ access, evaluations }) => {
          return canAddPeers
            ? access?.employeeName && (
                <Actions processTarget={access} currentPeerEvaluations={evaluations} />
              )
            : null
        },
      },
    ]),
    {
      variables: {
        performanceReviewProcessIds: [Number(reviewId)],
        searchValue: debouncedSearch || '',
      },
    }
  )

  return (
    <DataTable.Provider
      source={source}
      pagination={{
        kind: 'offset',
        itemsPerPage: 20,
      }}
    >
      <Box gap="s16" overflowY="hidden">
        <DataTable.HeadBar
          disableDefaultActions
          search={{
            placeholder: i18n.t('performance_process.reviews.target_management_search'),
            value: search,
            onChange: onSearch,
          }}
        />
        <DataTable.Table />
      </Box>
    </DataTable.Provider>
  )
}

export default ProcessTargetForPeerAssignmentDataTable
